import React, { useMemo, useState } from 'react';
import {
  Panel, PanelHead, ProgressBar, AvgChip, StatusDot, FamilyIcon, Avatar, Icon, Button,
} from '../../components/ui';
import { typeOfExamType } from '../../config/assessmentFamilies';
import { resultsForExamSet, statsFor } from './derive';
import { visibleSets, setCount } from './questionSets';
import { languageLabel, difficultyText, connectedToLabel } from './PositionHeader';

const ATTN_COLOUR = {
  below: ['var(--wz-below-bg)', 'var(--wz-below-fg)'],
  ai: ['var(--wz-fam-ai-bg)', 'var(--wz-fam-ai)'],
  lang: ['var(--wz-fam-lang-bg)', 'var(--wz-fam-lang)'],
  none: ['var(--wz-line-2)', 'var(--wz-ink-3)'],
};

function LinkButton({ children, onClick, style }) {
  return (
    <a href="#go" onClick={(e) => { e.preventDefault(); onClick(); }} style={{ fontSize: 13, fontWeight: 500, ...style }}>
      {children}
    </a>
  );
}

/** Prototype viewOverview(). */
export default function OverviewTab({
  campaign, people, examSets, hireBar, questions, codingQuestions,
  onGoTab, onOpenInterview, onOpenCandidate, onOpenQuestionSet, onNewInterview,
}) {
  const [descOpen, setDescOpen] = useState(false);
  const total = people.length;

  const funnel = useMemo(() => {
    const completed = people.filter((p) => p.all.some((r) => r.state === 'done' || r.state === 'scoring')).length;
    const above = people.filter((p) => p.avg != null && p.avg >= (p.hireBar ?? hireBar)).length;
    const short = people.filter((p) => p.stage === 'shortlisted').length;
    return [['Invited', total], ['Completed at least one interview', completed], ['Scored above the hire bar', above], ['Shortlisted', short]];
  }, [people, hireBar, total]);

  const ivRows = useMemo(() => examSets.map((es) => ({ es, s: statsFor(resultsForExamSet(people, es.id), hireBar) })), [examSets, people, hireBar]);

  const attention = useMemo(() => {
    const out = [];
    const scoring = people.reduce((n, p) => n + p.all.filter((r) => r.state === 'scoring').length, 0);
    if (scoring) {
      out.push({ icon: 'clock', c: 'below', text: `${scoring} completed interview${scoring > 1 ? 's are' : ' is'} still being scored`, action: 'Review candidates', go: () => onGoTab('candidates') });
    }
    ivRows.forEach(({ es, s }) => {
      if (s.notStarted) {
        out.push({ icon: 'msg', c: 'ai', text: `${s.notStarted} candidate${s.notStarted > 1 ? 's haven’t' : ' hasn’t'} started ${es.name}`, action: 'Send reminders', go: () => onOpenInterview(es.id) });
      }
      if (!s.total) out.push({ icon: 'user', c: 'lang', text: `${es.name} has no candidates yet`, action: 'Add candidates', go: () => onOpenInterview(es.id) });
    });
    visibleSets(questions, codingQuestions).filter((set) => !setCount(set, questions, codingQuestions)).forEach((set) => {
      out.push({ icon: 'book', c: 'none', text: `The ${set.name} question set is empty`, action: 'Generate questions', go: () => onOpenQuestionSet(set.id) });
    });
    return out;
  }, [people, ivRows, questions, codingQuestions, onGoTab, onOpenInterview, onOpenQuestionSet]);

  const leaders = useMemo(
    () => people.filter((p) => p.avg != null).sort((a, b) => b.avg - a.avg).slice(0, 3),
    [people],
  );

  const skills = Array.isArray(campaign.required_skills) ? campaign.required_skills.filter(Boolean) : [];
  const details = [
    ['Role', campaign.job_role || '—'],
    ['Job code', (campaign.job_code || '').trim() || '—'],
    ['Status', campaign.is_active ? 'Active' : 'Inactive'],
    ['Language', languageLabel(campaign.language)],
    ['Difficulty', difficultyText(campaign.difficulty_level)],
    ['Grading and hire bar', 'Set per batch'],
    ['Experience', campaign.experience_level || '—'],
    ['Connected to', connectedToLabel(campaign.source_tool) || 'Wizetok (Manual)'],
    ['Questions', `${campaign.questions_count ?? 0} · AI-generated`],
  ];

  return (
    <div className="wz-ov">
      <div className="wz-col">
        <Panel style={{ padding: '22px 24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
            <div>
              <h2>Hiring progress</h2>
              <p className="wz-sub" style={{ marginTop: 4 }}>Unique candidates across all interviews for this position</p>
            </div>
            <LinkButton onClick={() => onGoTab('candidates')}>View candidates</LinkButton>
          </div>
          <div className="wz-funnel">
            {funnel.map(([label, n]) => {
              const pct = total ? Math.round((n / total) * 100) : 0;
              return (
                <div className="wz-funnel-row" key={label}>
                  <span style={{ color: 'var(--wz-ink-2)' }}>{label}</span>
                  <ProgressBar pct={pct} height={10} />
                  <b style={{ fontSize: 15, textAlign: 'right' }}>{n}</b>
                  <span className="wz-sub" style={{ textAlign: 'right' }}>{pct}%</span>
                </div>
              );
            })}
          </div>
        </Panel>

        <Panel>
          <PanelHead>
            <h2>Interviews</h2>
            <span style={{ display: 'flex', gap: 16 }}>
              <LinkButton onClick={() => onGoTab('questions')}>View questions</LinkButton>
              <LinkButton onClick={() => onGoTab('interviews')}>Manage interviews</LinkButton>
            </span>
          </PanelHead>
          {ivRows.map(({ es, s }) => {
            const t = typeOfExamType(es.exam_type);
            const pct = s.total ? Math.round((s.done / s.total) * 100) : 0;
            return (
              <div className="wz-ivmini" key={es.id}>
                <button type="button" className="wz-rowlink" onClick={() => onOpenInterview(es.id)}>
                  <FamilyIcon fam={t.fam} />
                  <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <span className="wz-name">{es.name}</span>
                    <span className="wz-sub">{t.name}</span>
                  </span>
                </button>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span style={{ fontSize: 13, color: 'var(--wz-ink-2)' }}>{s.total ? `${s.done} of ${s.total} completed` : 'No candidates yet'}</span>
                  <ProgressBar pct={pct} />
                </div>
                <AvgChip value={s.avg} hireBar={s.hireBar} />
                <StatusDot live={es.is_active !== false} />
              </div>
            );
          })}
          {!ivRows.length && <p className="wz-sub" style={{ padding: '18px 24px' }}>No interviews yet.</p>}
        </Panel>

        <Panel style={{ padding: '22px 24px' }}>
          <h2>Position details</h2>
          <dl className="wz-details">
            {details.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}
            {skills.length > 0 && (
              <div style={{ gridColumn: '1 / -1' }}>
                <dt>Required skills</dt>
                <dd style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 2 }}>
                  {skills.map((sk) => <span key={sk} className="wz-bpill">{sk}</span>)}
                </dd>
              </div>
            )}
          </dl>
          {campaign.job_description ? (
            <div className="wz-desc">
              <h3 className="wz-sub" style={{ fontWeight: 500, marginBottom: 8 }}>Description</h3>
              <p style={descOpen ? null : { maxHeight: '4.8em', overflow: 'hidden' }}>{campaign.job_description}</p>
              <Button small style={{ marginTop: 12 }} aria-expanded={descOpen} onClick={() => setDescOpen((v) => !v)}>
                {descOpen ? 'Show less' : 'Show full description'}
              </Button>
            </div>
          ) : null}
        </Panel>
      </div>

      <div className="wz-col">
        <Panel>
          <PanelHead style={{ padding: '18px 20px' }}><h2>Needs your attention</h2></PanelHead>
          <ul className="wz-attn" style={{ padding: '6px 0' }}>
            {attention.length ? attention.map((a) => (
              <li key={a.text}>
                <span className="ic" style={{ background: ATTN_COLOUR[a.c][0], color: ATTN_COLOUR[a.c][1] }}><Icon name={a.icon} /></span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ lineHeight: 1.4 }}>{a.text}</span>
                  <LinkButton onClick={a.go}>{a.action}</LinkButton>
                </div>
              </li>
            )) : <li className="wz-sub">Nothing needs attention right now.</li>}
          </ul>
        </Panel>

        <Panel>
          <PanelHead style={{ padding: '18px 20px' }}>
            <h2>Leading candidates</h2>
            <span className="wz-sub" style={{ fontSize: 12 }}>Average score</span>
          </PanelHead>
          <ol className="wz-leaders" style={{ padding: '6px 0' }}>
            {leaders.map((p, i) => (
              <li key={p.key}>
                <span className="wz-sub" style={{ width: 18, textAlign: 'right' }}>{i + 1}</span>
                <Avatar name={p.name} size={32} />
                <button type="button" className="wz-rowlink" style={{ flex: 1 }} onClick={() => onOpenCandidate(p.key)}>
                  <span style={{ display: 'flex', flexDirection: 'column' }}>
                    <span className="wz-name" style={{ fontWeight: 500 }}>{p.name}</span>
                    <span className="wz-sub" style={{ fontSize: 12 }}>{p.scoredCount} of {p.assignedCount} interviews scored</span>
                  </span>
                </button>
                <AvgChip value={p.avg} hireBar={p.hireBar} />
              </li>
            ))}
            {!leaders.length && <li className="wz-sub">No scored candidates yet.</li>}
          </ol>
        </Panel>

        <Panel style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <h2 style={{ marginBottom: 4 }}>Quick actions</h2>
          <Button need="candidate.add" icon="userAdd" style={{ justifyContent: 'flex-start' }} onClick={() => onGoTab('interviews')}>Add candidates</Button>
          <Button need="exam.manage" icon="plus" style={{ justifyContent: 'flex-start' }} onClick={onNewInterview}>New interview</Button>
          <Button icon="file" style={{ justifyContent: 'flex-start' }} onClick={() => onGoTab('candidates')}>View reports</Button>
        </Panel>
      </div>
    </div>
  );
}
